import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ActivityInfo from "src/sections/pages/activity-page/activity-info";
import axios from "../../../../axios";
import SideBar from "../../SideBar";
import Notification from "../components/Notification";
import PreviewOverlay from "../components/PreviewOverlay";
import Title from "../components/Title";
import LangInputs from "./LangInputs";
import DescriptionInputs from "./DescriptionInputs";
import ImagesInput from "./ImagesInput";
import RequestFieldsInput from "../components/RequestFieldsInput";
import TourFieldsInput from "./TourFieldsInput";

// Все языки сайта. Важно: при редактировании копируем объект целиком,
// иначе язык, которого нет в этом списке, потерялся бы при сохранении.
const LANGS = ["en", "ru", "kk"];

const langObject = (source) => {
  const result = { ...(source || {}) };
  for (const lang of LANGS) if (!result[lang]) result[lang] = "";
  return result;
};


// Мультиязычное значение считается заполненным, если хоть один язык не пуст.
const hasText = (obj) => Object.values(obj || {}).some((v) => String(v || "").trim());
const dropEmptyLang = (list) => (list || []).filter(hasText);

export default function EditActivity() {
  const location = useLocation();
  const passedProps = location.state;
  const navigate = useNavigate();

  const [descNum, setDescNum] = useState(passedProps.descriptions.length);
  const [images, setImages] = useState(
    passedProps.images?.length
      ? passedProps.images
      : passedProps.imageUrl
      ? [passedProps.imageUrl]
      : []
  );
  const [show, setShow] = useState(false);
  const [text, setText] = useState("Успешно сохранено!");
  const [subText, setSubText] = useState(
    "Перенаправляем вас на главную страницу."
  );
  const [state, setState] = useState("success");
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const increaseDescNum = () => {
    if (descNum < 4) {
      setDescNum((prev) => prev + 1);
      setFormData((prevFormData) => ({
        ...prevFormData,
        descriptions: [
          ...prevFormData.descriptions,
          { name: "", description: "" },
        ],
      }));
    }
  };

  const decreaseDescNum = () => {
    if (descNum > 1) {
      setDescNum((prev) => prev - 1);
      setFormData((prevFormData) => ({
        ...prevFormData,
        descriptions: prevFormData.descriptions.slice(0, -1),
      }));
    }
  };

  const defaultDescription = {
    name: langObject(),
    description: langObject(),
  };

  const [formData, setFormData] = useState({
    name: langObject(passedProps.name),
    place: langObject(passedProps.place),
    text: langObject(passedProps.text),
    date: passedProps.date || "",
    price: passedProps.price || "",
    category: langObject(passedProps.category),
    descriptions: passedProps.descriptions
      ? passedProps.descriptions.map((desc) => ({
          name: langObject(desc.name),
          description: langObject(desc.description),
          highlight: Boolean(desc.highlight),
        }))
      : [defaultDescription],
    coordinates: passedProps.coordinates || { lat: "", lng: "" },
    serviceIds: passedProps.serviceIds || [],
    requestFields: passedProps.requestFields || {},
    // Поля японского тура — редактируются блоком TourFieldsInput ниже.
    dateEnd: passedProps.dateEnd || "",
    route: passedProps.route || [],
    priceNote: langObject(passedProps.priceNote),
    deposit: passedProps.deposit || "",
    depositNote: langObject(passedProps.depositNote),
    itinerary: passedProps.itinerary || [],
    included: passedProps.included || [],
    notIncluded: passedProps.notIncluded || [],
    extras: passedProps.extras || [],
    ctaType: passedProps.ctaType || "form",
    ctaLabel: langObject(passedProps.ctaLabel),
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (["date", "dateEnd", "price", "deposit"].includes(name)) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    } else {
      const [fieldName, lang] = name.split(".");

      setFormData((prevData) => ({
        ...prevData,
        [fieldName]: {
          ...prevData[fieldName],
          [lang]: value,
        },
      }));
    }
  };

  const handleDescriptionNameChange = (index, value, lang) => {
    const updatedDescriptions = [...formData.descriptions];
    if (!updatedDescriptions[index].name) {
      updatedDescriptions[index].name = { [lang]: value };
    } else {
      updatedDescriptions[index].name[lang] = value;
    }
    setFormData({
      ...formData,
      descriptions: updatedDescriptions,
    });
  };

  const handleDescriptionDescriptionChange = (index, value, lang) => {
    const updatedDescriptions = [...formData.descriptions];
    if (!updatedDescriptions[index].description) {
      updatedDescriptions[index].description = { [lang]: value };
    } else {
      updatedDescriptions[index].description[lang] = value;
    }
    setFormData({
      ...formData,
      descriptions: updatedDescriptions,
    });
  };

  const handleDescriptionHighlightChange = (index, checked) => {
    setFormData((prev) => ({
      ...prev,
      descriptions: prev.descriptions.map((d, i) =>
        i === index ? { ...d, highlight: checked } : d
      ),
    }));
  };

  const notifyUploadError = () => {
    setShow(true);
    setText("Не удалось загрузить файл!");
    setSubText("Попробуйте файл другого типа!");
    setState("warn");
    setTimeout(() => setShow(false), 3500);
  };

  const handleCancelClick = () => {
    navigate("/admin/activities");
  };

  const getNestedValue = (obj, path) => {
    return path
      .split(".")
      .reduce(
        (acc, curr) => (acc && acc[curr] !== undefined ? acc[curr] : null),
        obj
      );
  };

  // То же самое уходит и в предпросмотр, и в базу — клиент видит ровно
  // то, что сохранится.
  const buildPayload = () => ({
    ...formData,
    images,
    imageUrl: images[0] || "", // cover, for backward compatibility
    descriptions: formData.descriptions.filter((d) =>
      [...Object.values(d.name || {}), ...Object.values(d.description || {})].some(Boolean)
    ),
    // Пустые строки, оставшиеся после «Добавить пункт», в базу не отправляем.
    route: dropEmptyLang(formData.route),
    included: dropEmptyLang(formData.included),
    notIncluded: dropEmptyLang(formData.notIncluded),
    itinerary: (formData.itinerary || []).filter(
      (d) => d?.day || hasText(d?.title) || hasText(d?.text)
    ),
    extras: (formData.extras || []).filter((e) => hasText(e?.label)),
  });

  // Перед сохранением показываем предпросмотр — сохраняем только после «ок».
  const handlePreviewClick = () => {
    // Only the tour name is required; everything else is optional.
    const requiredFields = [
      { path: "name.en", label: "Название (English)" },
      { path: "name.ru", label: "Название (Русский)" },
    ];

    const missing = requiredFields
      .filter((f) => !getNestedValue(formData, f.path))
      .map((f) => f.label);

    if (missing.length > 0) {
      setShow(true);
      setText("Заполните обязательные поля:");
      setSubText(missing.join(", "));
      setState("warn");
      setTimeout(() => setShow(false), 4000);
      return;
    }

    setPreviewing(true);
  };

  const handleEditClick = async () => {
    setSaving(true);
    try {
      await axios.patch(`/activities/${passedProps._id}`, buildPayload());

      setPreviewing(false);
      setShow(true);
      setText("Активность обновлена успешно!");
      setSubText("Перенаправляем вас на главную страницу!");
      setState("success");

      setTimeout(() => {
        setShow(false);
        navigate("/admin/activities");
      }, 3500);
    } catch (error) {
      setPreviewing(false);
      setShow(true);
      setText("Ошибка сохранения");
      setSubText("Попробуйте ещё раз.");
      setState("error");
      setTimeout(() => setShow(false), 3500);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {previewing && (
        <PreviewOverlay
          title="Так тур увидит клиент"
          confirmLabel="Всё верно — сохранить"
          saving={saving}
          onBack={() => setPreviewing(false)}
          onConfirm={handleEditClick}
        >
          <ActivityInfo activity={buildPayload()} />
        </PreviewOverlay>
      )}
      <SideBar>
        <Notification
          show={show}
          setShow={setShow}
          text={text}
          subText={subText}
          state={state}
        />
        <Title text="Редактировать активность" />
        <div className="w-full flex justify-start items-center">
          <div className="p-4 rounded-md max-w-3xl w-full relative">
            <div className="w-full flex flex-col justify-start items-start gap-y-4 md:px-4">
              <LangInputs
                label="Название"
                field="name"
                values={formData.name}
                onChange={handleInputChange}
                placeholders={{
                  en: "Cherry blossom in Kyoto",
                  ru: "Сакура в Киото",
                  kk: "Киотодағы сакура",
                }}
              />
              <LangInputs
                label="Место"
                field="place"
                values={formData.place}
                onChange={handleInputChange}
                placeholders={{
                  en: "Japan / Kyoto",
                  ru: "Япония / Киото",
                  kk: "Жапония / Киото",
                }}
              />
              <LangInputs
                label="Краткое описание"
                field="text"
                values={formData.text}
                onChange={handleInputChange}
                textarea
                placeholders={{ en: "Description", ru: "Описание", kk: "Сипаттама" }}
              />
              <div className="w-full flex flex-col md:flex-row justify-start items-start gap-y-4 gap-x-4">
                <div className="w-full">
                  <label
                    htmlFor="date"
                    className="block text-sm font-medium text-neutral-700"
                  >
                    Дата <span className="text-neutral-400">(необязательно)</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    id="date"
                    className="mt-1 w-full rounded-md border-0 py-1.5 px-4 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="23-11-2023"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="w-full">
                  <label
                    htmlFor="price"
                    className="block text-sm font-medium text-neutral-700"
                  >
                    Цена
                  </label>
                  <input
                    type="text"
                    name="price"
                    id="price"
                    className="mt-1 w-full rounded-md border-0 py-1.5 px-4 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="35,000 ₸"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <LangInputs
                label="Категория"
                field="category"
                values={formData.category}
                onChange={handleInputChange}
                placeholders={{ en: "Tours", ru: "Туры", kk: "Турлар" }}
              />
              <div className="w-full flex justify-between items-center">
                <div className="block text-base font-medium text-neutral-700">
                  Расширенное описание
                </div>
                <div className="flex items-center gap-x-4">
                  <button
                    type="button"
                    onClick={increaseDescNum}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
                  >
                    Добавить
                  </button>
                  <button
                    type="button"
                    onClick={decreaseDescNum}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                  >
                    Убрать
                  </button>
                </div>
              </div>
              {formData.descriptions.map((desc, index) => (
                <DescriptionInputs
                  key={index}
                  index={index}
                  desc={desc}
                  onNameChange={handleDescriptionNameChange}
                  onDescChange={handleDescriptionDescriptionChange}
                  onHighlightChange={handleDescriptionHighlightChange}
                />
              ))}
              <ImagesInput
                value={images}
                onChange={setImages}
                onError={notifyUploadError}
              />
              <TourFieldsInput
                value={formData}
                onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              />
              <RequestFieldsInput
                value={formData.requestFields}
                onChange={(requestFields) =>
                  setFormData((prev) => ({ ...prev, requestFields }))
                }
              />
              <div className="w-full flex justify-end items-center gap-x-4">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="self-end inline-flex items-center px-4 py-2 border border-neutral-300 text-base font-medium rounded-md shadow-sm text-neutral-900 hover:bg-neutral-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-500"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handlePreviewClick}
                  className="self-end inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
                >
                  Предпросмотр и сохранить
                </button>
              </div>
            </div>
          </div>
        </div>
      </SideBar>
    </>
  );
}
